import { useState, useEffect, useMemo, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import InfoHint from './InfoHint';
import { TrendingUp, Filter, LineChart, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LabelList,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { fetchCallDynamics, fetchConversionDynamics } from '../api';
import useNodeChartItems from './nodes/useNodeChartItems';
import SectionGrid from './layout/SectionGrid';
import PlotBox from './layout/PlotBox';
import { buildTabDefaults } from '../config/sectionLayouts';
import useNodeCatalog from '../hooks/useNodeCatalog';
import CallsByEmployeeChart from './CallsByEmployeeChart';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import useTimeZone from '../hooks/useTimeZone';
import useLatestRequest from '../hooks/useLatestRequest';
import { formatDayMonthYear } from '../utils/dateTime';
import { useIsMobile } from '../hooks/useMediaQuery';

const defaultRange = {
  from: subDays(new Date(), 29),
  to: new Date()
};

/** Высота графика «Динамики звонков» вне сетки и нижняя граница в ней — бывшие `h-64`. */
const DYNAMICS_PLOT_HEIGHT = 256;

const callsAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : value ?? '—';

const callDynamicsTooltipFormatter = (entry) => {
  if (!entry || typeof entry.value !== 'number') {
    return '—';
  }

  return `${entry.value.toLocaleString('ru-RU')}`;
};

const createValueLabelRenderer = (color, formatter) => {
  const ChartValueLabel = (props) => {
    const { x, y, value, width } = props;
    if (value === null || value === undefined) {
      return null;
    }

    const baseX = typeof x === 'number' ? x : 0;
    const centerX = typeof width === 'number' ? baseX + width / 2 : baseX;

    return (
      <text
        x={centerX}
        y={(y ?? 0) - 8}
        textAnchor="middle"
        fill={color}
        fontSize="9"
        fontWeight="600"
      >
        {formatter ? formatter(value) : value}
      </text>
    );
  };

  // Имя нужно не только линтеру: recharts клонирует этот элемент, и в React DevTools
  // безымянная стрелка выглядит как anonymous среди десятка таких же.
  ChartValueLabel.displayName = 'ChartValueLabel';

  return ChartValueLabel;
};

const tooltipWrapperStyle = { zIndex: 30 };

const formatCallDynamicsLabel = (point, timeZone) => {
  if (!point) {
    return '—';
  }

  const granularity = point.granularity?.toLowerCase();
  if (granularity === 'hour' || granularity === 'minute') {
    return point.label ?? '—';
  }

  const raw = point.day ?? point.label;
  if (!raw) {
    return '—';
  }

  if (raw instanceof Date || typeof raw === 'string') {
    const formatted = formatDayMonthYear(raw, timeZone);
    return formatted === '—' ? String(raw) : formatted;
  }

  return typeof raw === 'string' ? raw : String(raw);
};

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md max-sm:max-w-[240px] max-sm:text-xs"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span>
            {entry.name ?? entry.dataKey}: {formatter ? formatter(entry) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const GeneralStatistics = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [chartType, setChartType] = useState('bar');
  const isMobile = useIsMobile();
  const [callDynamics, setCallDynamics] = useState([]);
  const [conversionDynamics, setConversionDynamics] = useState([]);
  const [isLoadingDynamics, setIsLoadingDynamics] = useState(false);
  const [error, setError] = useState(null);
  const dynamicsRequests = useLatestRequest();
  const conversionRequests = useLatestRequest();
  const { timeZone } = useTimeZone();
  const panels = usePanelSettings();
  const isDynamicsVisible = panels.isVisible(PANEL_IDS['ui:dashboard.call-dynamics']);

  const activeDateRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return dateRange;
    }

    return defaultRange;
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return 'за выбранный период';
    }

    try {
      const fromLabel = formatDayMonthYear(activeDateRange.from, timeZone);
      const toLabel = formatDayMonthYear(activeDateRange.to, timeZone);
      return `за период ${fromLabel} — ${toLabel}`;
    } catch {
      return 'за выбранный период';
    }
  }, [activeDateRange, timeZone]);

  const loadCallDynamics = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return;
    }

    const request = dynamicsRequests.begin({ background: showSpinner === false });
    if (!request) {
      return;
    }

    if (showSpinner) {
      setIsLoadingDynamics(true);
    }

    setError(null);
    try {
      // Разрез по теме отсюда убран вместе с селектом: `call_classification_type` — колонка
      // легаси-движка, в режиме нод она не заполняется, и список тем всегда был пуст.
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchCallDynamics(params);
      if (request.isCurrent()) {
        setCallDynamics(data ?? []);
      }
    } catch {
      if (request.isCurrent()) {
        setError('Не удалось загрузить динамику звонков.');
      }
    } finally {
      const isCurrent = request.isCurrent();
      request.end();
      if (showSpinner && isCurrent) {
        setIsLoadingDynamics(false);
      }
    }
  }, [activeDateRange, callType, dynamicsRequests]);

  // `background` — опрос по таймеру: он пропускается, пока не вернулся последний запрос.
  const loadConversionDynamics = useCallback(async (background = false) => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return;
    }

    const request = conversionRequests.begin({ background });
    if (!request) {
      return;
    }

    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchConversionDynamics(params);
      if (request.isCurrent()) {
        setConversionDynamics(Array.isArray(data) ? data : []);
      }
    } catch {
      if (request.isCurrent()) {
        setConversionDynamics([]);
      }
    } finally {
      request.end();
    }
  }, [activeDateRange, callType, conversionRequests]);

  // Скрытая карточка не должна опрашивать сервер: обе выборки питают только её, и без этой
  // проверки спрятанный график продолжал бы ходить в API каждые GRAPH_REFRESH_INTERVAL_MS.
  useEffect(() => {
    if (isDynamicsVisible) {
      loadCallDynamics();
    }
  }, [loadCallDynamics, isDynamicsVisible]);

  useEffect(() => {
    if (isDynamicsVisible) {
      loadConversionDynamics();
    }
  }, [loadConversionDynamics, isDynamicsVisible]);

  useEffect(() => {
    if (!isDynamicsVisible) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      loadCallDynamics(false);
      loadConversionDynamics(true);
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadCallDynamics, loadConversionDynamics, isDynamicsVisible]);

  const maxCalls = useMemo(() => {
    if (!callDynamics.length) return 1;
    return Math.max(
      ...callDynamics.map((point) =>
        Math.max(point.calls ?? 0, point.incomingCalls ?? 0, point.outgoingCalls ?? 0)
      )
    );
  }, [callDynamics]);

  const dynamicsUnitLabel = useMemo(() => {
    const granularity = callDynamics?.[0]?.granularity;
    if (granularity === 'minute') return 'минуту';
    if (granularity === 'hour') return 'час';
    return 'день';
  }, [callDynamics]);

  const lineChartData = useMemo(
    () =>
      (callDynamics ?? []).map((point) => {
        const totalCalls = point.calls ?? 0;
        const incomingCalls = point.incomingCalls ?? 0;
        const outgoingCalls = point.outgoingCalls ?? 0;
        return {
          label: formatCallDynamicsLabel(point, timeZone),
          calls: totalCalls,
          incomingCalls,
          outgoingCalls
        };
      }),
    [callDynamics, timeZone]
  );

  const conversionChartData = useMemo(
    () =>
      (conversionDynamics ?? []).map((point) => ({
        label: formatCallDynamicsLabel(point, timeZone),
        conversionRate: typeof point.conversionRate === 'number' ? point.conversionRate : Number(point.conversionRate) || 0,
        applicableCalls: point.applicableCalls ?? 0,
        bookingAttempts: point.bookingAttempts ?? 0,
        bookingSuccesses: point.bookingSuccesses ?? 0
      })),
    [conversionDynamics, timeZone]
  );

  // Каталог читается здесь, а не внутри графиков: из него же строится раскладка по умолчанию.
  const nodeCatalog = useNodeCatalog();
  const nodeItems = useNodeChartItems({
    tab: 'dashboard',
    nodeCatalog,
    dateRange: activeDateRange,
    callType,
    onOpenCallJournal
  });

  // Карточка растягивается по высоте ячейки сетки: flex-колонка до области графика (PlotBox).
  const dynamicsCard = isDynamicsVisible && (
      <Card className="flex flex-col" data-panel-id={PANEL_IDS['ui:dashboard.call-dynamics']}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 max-sm:flex-col max-sm:items-start max-sm:gap-3">
          <div>
            <div className="flex items-center gap-1.5">
              <CardTitle>{panels.title(PANEL_IDS['ui:dashboard.call-dynamics'])}</CardTitle>
              {/* Подпись считает период и потому переименованию не поддаётся — в реестре её нет. */}
              <InfoHint
                text={`Всего звонков ${dateRangeLabel}${callDynamics.length > 0 ? `. Пик нагрузки: ${maxCalls.toLocaleString()} звонков/${dynamicsUnitLabel}` : ''}`}
                label="О динамике звонков"
                trackId="dashboard.dynamics.hint"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 max-sm:justify-start">
            <ChartModeToggle chartType={chartType} onChange={setChartType} />
            <ButtonReload loading={isLoadingDynamics} onReload={loadCallDynamics} />
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col">
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          {chartType === 'conversion' && conversionChartData.length === 0 && (
            <p className="text-sm text-muted-foreground">Нет данных о конверсии за выбранный период.</p>
          )}
          {chartType === 'conversion' && conversionChartData.length > 0 && (
                <PlotBox height={DYNAMICS_PLOT_HEIGHT} className="rounded-md bg-muted/30" contentClassName="px-2 py-4">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                    <ReLineChart data={conversionChartData} margin={{ top: 12, right: isMobile ? 8 : 24, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis
                        width={isMobile ? 40 : 60}
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                        tick={{ fontSize: 11 }}
                      />
                      <RechartsTooltip
                        wrapperStyle={tooltipWrapperStyle}
                        content={<ConversionTooltip />}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line
                        type="monotone"
                        dataKey="conversionRate"
                        name="Конверсия в целевое действие"
                        stroke="#16a34a"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 4 }}
                      >
                        {/* На телефоне подписи у точек не рисуем: при 30 днях это 5px на точку,
                            подписи сливаются. Значение остаётся в тултипе. */}
                        {!isMobile && (
                          <LabelList
                            dataKey="conversionRate"
                            content={createValueLabelRenderer('#16a34a', (value) =>
                              typeof value === 'number' ? `${value.toFixed(1)}%` : value
                            )}
                          />
                        )}
                      </Line>
                    </ReLineChart>
                  </ResponsiveContainer>
                </PlotBox>
          )}
          {chartType !== 'conversion' && callDynamics.length === 0 && !isLoadingDynamics && (
            <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
          )}
          {chartType !== 'conversion' && callDynamics.length > 0 && (
            <PlotBox height={DYNAMICS_PLOT_HEIGHT} className="rounded-md bg-muted/30" contentClassName="px-2 py-4">
              {chartType === 'bar' ? (
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                      <BarChart data={lineChartData} margin={{ top: 12, right: isMobile ? 8 : 24, left: 0, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          width={isMobile ? 40 : 60}
                          domain={[0, Math.ceil(1.1 * maxCalls)]}
                          tickFormatter={callsAxisFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <RechartsTooltip
                          wrapperStyle={tooltipWrapperStyle}
                          content={<ChartTooltip formatter={callDynamicsTooltipFormatter} />}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar
                          dataKey="calls"
                          name="Звонки"
                          fill="#2563eb"
                          radius={[4, 4, 0, 0]}
                        >
                          {!isMobile && (
                            <LabelList
                              dataKey="calls"
                              content={createValueLabelRenderer('#0f172a', callsAxisFormatter)}
                            />
                          )}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
              ) : (
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                      <ReLineChart data={lineChartData} margin={{ top: 12, right: isMobile ? 8 : 24, left: 0, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          width={isMobile ? 40 : 60}
                          domain={[0, Math.ceil(1.1 * maxCalls)]}
                          tickFormatter={callsAxisFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <RechartsTooltip
                          wrapperStyle={tooltipWrapperStyle}
                          content={<ChartTooltip formatter={callDynamicsTooltipFormatter} />}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="calls"
                          name="Звонки"
                          stroke="#2563eb"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        >
                          {!isMobile && (
                            <LabelList
                              dataKey="calls"
                              content={createValueLabelRenderer('#2563eb', callsAxisFormatter)}
                            />
                          )}
                        </Line>
                      </ReLineChart>
                    </ResponsiveContainer>
              )}
            </PlotBox>
          )}
        </CardContent>
      </Card>
  );

  const entries = [
    dynamicsCard && { id: PANEL_IDS['ui:dashboard.call-dynamics'], element: dynamicsCard },
    // Оператор — метаданные звонка, а не выход узла, поэтому карточка не из каталога пакета.
    // Гард стоит здесь, а не внутри карточки: при скрытии она должна размонтироваться, иначе
    // её собственный опрос продолжает идти в API.
    panels.isVisible(PANEL_IDS['ui:dashboard.calls-by-employee']) && {
      id: PANEL_IDS['ui:dashboard.calls-by-employee'],
      element: (
        <CallsByEmployeeChart dateRange={activeDateRange} callType={callType} onOpenCallJournal={onOpenCallJournal} />
      )
    },
    // Графики по активному пакету узлов. Чек-листы качества сюда не входят — они живут на
    // вкладке «Сотрудники», потому что читаются в разрезе операторов.
    ...nodeItems.entries
  ].filter(Boolean);

  const defaults = buildTabDefaults('dashboard', {
    nodeSections: nodeItems.nodeSections,
    hiddenNodeItems: nodeItems.hiddenNodeItems
  });

  return (
    <div className="flex flex-col gap-6">
      <SectionGrid
        tab="dashboard"
        entries={entries}
        defaults={defaults}
        hiddenEntries={nodeItems.hiddenEntries}
      />
      {nodeItems.notice}
    </div>
  );
};

const ConversionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload ?? {};

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md max-sm:max-w-[240px] max-sm:text-xs"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      <div>Конверсия: {Number(point.conversionRate ?? 0).toFixed(1)}%</div>
      <div>Применимые: {Number(point.applicableCalls ?? 0).toLocaleString('ru-RU')}</div>
      <div>Попытки результата: {Number(point.bookingAttempts ?? 0).toLocaleString('ru-RU')}</div>
      <div>Достигнуто: {Number(point.bookingSuccesses ?? 0).toLocaleString('ru-RU')}</div>
    </div>
  );
};

const ButtonReload = ({ loading, onReload }) => (
  <button
    type="button"
    data-ev="dashboard.dynamics.reload"
    onClick={onReload}
    disabled={loading}
    className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50 max-sm:h-8"
    aria-label={loading ? 'Обновление' : 'Обновить'}
  >
    <Filter className="h-4 w-4 sm:mr-2" />
    <span className="max-sm:hidden">{loading ? 'Обновление...' : 'Обновить'}</span>
  </button>
);

const CHART_MODES = [
  { id: 'bar', label: 'Столбцы', icon: BarChart3 },
  { id: 'line', label: 'Линия', icon: LineChart },
  { id: 'conversion', label: 'Конверсия', icon: TrendingUp }
];

const ChartModeToggle = ({ chartType, onChange }) => (
  <div className="inline-flex rounded-md border">
    {CHART_MODES.map((mode, index) => {
      const Icon = mode.icon;
      const isActive = chartType === mode.id;
      return (
        <button
          key={mode.id}
          type="button"
          data-ev="dashboard.dynamics.mode"
          data-ev-value={mode.id}
          onClick={() => onChange(mode.id)}
          aria-label={mode.label}
          aria-pressed={isActive}
          className={`inline-flex items-center px-3 py-1 text-sm max-sm:h-8 ${
            index === 0 ? 'rounded-l-md' : ''
          } ${index === CHART_MODES.length - 1 ? 'rounded-r-md' : ''} ${
            isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          {/* На телефоне — только иконки: три кнопки с подписями шире карточки. */}
          <Icon className="h-4 w-4 sm:mr-2" />
          <span className="max-sm:hidden">{mode.label}</span>
        </button>
      );
    })}
  </div>
);

export default GeneralStatistics;
